// CaseSidebar — LEFT pane of the two-pane DataWash layout.
//
// Shows cases organized by user-created GROUPS (collapsible folders).
// Ungrouped cases appear in their own section. Archived cases appear
// under the "Archive" group (or are hidden unless "Show archived" is on).
// Each case row has a context menu (right-click or "..." button) with:
//   - Archive / Unarchive
//   - Assign to... (analyst/reviewer/operator)
//   - Move to group... (pick a group)
// Drag a case row onto a group header to assign it to that group.
// "+ New Group" creates a group with an inline name input.

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Search, FileText, GitCompare, AlertTriangle, Plus, Loader2, ShieldCheck,
  Settings, ChevronRight, ChevronDown, MoreVertical, Archive, ArchiveRestore,
  UserPlus, FolderInput, Trash2, FolderPlus,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { listCases, updateCase, type Case, type CaseListParams, type CaseType } from '../api/cases';
import {
  listCaseGroups, createCaseGroup, deleteCaseGroup, assignCaseToGroup,
  removeCaseFromGroup, listAssignableUsers,
  type CaseGroup, type AssignableUser,
} from '../api/caseGroups';
import { useToastStore } from '../stores/toast';
import { RC_LABEL } from '../config/labels';
import AboutModal, { type AboutInfo } from './AboutModal';

export type CaseFilter = 'all' | CaseType;

interface CaseSidebarProps {
  activeCaseId: string | null;
  onSelectCase: (c: Case) => void;
  onNewCase: () => void;
  refreshKey?: number;
  width?: number;
}

// 'Deficiencies' filter hidden per operator direction 2026-10-15 (keep the
// has_deficiencies backend filter + CaseFilter type intact; just remove the
// visible tab). Restore = re-add `{ key: 'deficiency', label: 'Deficiencies' }`.
const FILTER_TABS: { key: CaseFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'deposit_review', label: 'Deposits' },
  { key: 'comparison', label: 'Comparisons' },
];

const ARCHIVE_GROUP_NAME = 'Archive';

function typeIcon(type?: CaseType) {
  if (type === 'comparison') return <GitCompare size={16} className="text-violet-600 flex-shrink-0" />;
  if (type === 'deficiency') return <AlertTriangle size={16} className="text-amber-600 flex-shrink-0" />;
  return <FileText size={16} className="text-primary-600 flex-shrink-0" />;
}

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day} days ago`;
  return new Date(iso).toLocaleDateString();
}

interface ContextMenuState {
  caseId: string;
  x: number;
  y: number;
}

export default function CaseSidebar({ activeCaseId, onSelectCase, onNewCase, refreshKey, width }: CaseSidebarProps) {
  const navigate = useNavigate();
  const toast = useToastStore();
  const [cases, setCases] = useState<Case[]>([]);
  const [groups, setGroups] = useState<CaseGroup[]>([]);
  const [assignable, setAssignable] = useState<AssignableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<CaseFilter>('all');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [showArchived, setShowArchived] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [assignSubmenu, setAssignSubmenu] = useState(false);
  const [moveSubmenu, setMoveSubmenu] = useState(false);
  const [dragOverGroup, setDragOverGroup] = useState<string | null>(null);
  const [envProfile, setEnvProfile] = useState('');
  const [aboutInfo, setAboutInfo] = useState<AboutInfo | null>(null);
  const [showAbout, setShowAbout] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadGroups = useCallback(async () => {
    try {
      let gs = await listCaseGroups();
      if (gs.length === 0) {
        try {
          await createCaseGroup(ARCHIVE_GROUP_NAME);
          gs = await listCaseGroups();
        } catch { /* best-effort */ }
      }
      setGroups(gs);
    } catch {
      setGroups([]);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params: CaseListParams = { page: 1, page_size: 100 };
    if (filter === 'deficiency') params.has_deficiencies = true;
    else if (filter !== 'all') params.type = filter;
    if (search.trim()) params.search = search.trim();
    if (mineOnly) params.mine_only = true;
    if (showArchived) params.archived = true;
    listCases(params)
      .then((resp) => setCases(resp.items || []))
      .catch((e) => setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed to load cases'))
      .finally(() => setLoading(false));
  }, [search, filter, refreshKey, showArchived, mineOnly]);

  useEffect(() => {
    loadGroups();
    listAssignableUsers().then(setAssignable).catch(() => setAssignable([]));
  }, [loadGroups, refreshKey]);

  useEffect(() => {
    const base = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL || '/api/v1';
    fetch(`${base}/health`)
      .then((r) => r.json())
      .then((d: { profile?: string; rc_version?: string; version?: string }) => {
        if (d.profile) setEnvProfile(d.profile);
        setAboutInfo({ profile: d.profile || 'dev', rc_version: d.rc_version || 'dev', version: d.version || 'dev' });
      })
      .catch(() => { /* best-effort */ });
  }, []);

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => { setContextMenu(null); setAssignSubmenu(false); setMoveSubmenu(false); };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [contextMenu]);

  const onSearchChange = (val: string) => {
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(val), 250);
  };

  const toggleGroup = (gid: string) =>
    setCollapsed((p) => ({ ...p, [gid]: !p[gid] }));

  const caseToGroups: Record<string, string[]> = {};
  groups.forEach((g) => {
    (g.case_ids || []).forEach((cid) => {
      (caseToGroups[cid] ||= []).push(g.id);
    });
  });

  const archiveGroup = groups.find((g) => g.name === ARCHIVE_GROUP_NAME);
  const archiveGroupId = archiveGroup?.id || null;
  const regularGroups = groups.filter((g) => g.name !== ARCHIVE_GROUP_NAME);

  const archivedCases = cases.filter((c) => c.is_archived);
  const activeCases = cases.filter((c) => !c.is_archived);

  const casesByGroup: Record<string, Case[]> = {};
  const ungrouped: Case[] = [];
  activeCases.forEach((c) => {
    const gids = caseToGroups[c.id] || [];
    const nonArchive = gids.filter((gid) => gid !== archiveGroupId);
    if (nonArchive.length === 0) {
      ungrouped.push(c);
    } else {
      nonArchive.forEach((gid) => {
        (casesByGroup[gid] ||= []).push(c);
      });
    }
  });

  const reloadAll = useCallback(async () => {
    await loadGroups();
    const params: CaseListParams = { page: 1, page_size: 100 };
    if (filter === 'deficiency') params.has_deficiencies = true;
    else if (filter !== 'all') params.type = filter;
    if (search.trim()) params.search = search.trim();
    if (mineOnly) params.mine_only = true;
    if (showArchived) params.archived = true;
    try {
      const resp = await listCases(params);
      setCases(resp.items || []);
    } catch { /* keep */ }
  }, [loadGroups, filter, search, mineOnly, showArchived]);

  const toggleArchive = useCallback(async (c: Case) => {
    try {
      await updateCase(c.id, { is_archived: !c.is_archived });
      toast.addToast(c.is_archived ? 'Case unarchived' : 'Case archived');
      await reloadAll();
    } catch (e: any) {
      toast.addToast(e?.response?.data?.detail || 'Archive toggle failed', 'error');
    }
  }, [reloadAll, toast]);

  const assignTo = useCallback(async (c: Case, userId: string | null) => {
    try {
      await updateCase(c.id, { assigned_analyst_id: userId });
      toast.addToast(userId ? 'Case assigned' : 'Case unassigned');
      await reloadAll();
    } catch (e: any) {
      toast.addToast(e?.response?.data?.detail || 'Assignment failed', 'error');
    }
  }, [reloadAll, toast]);

  const moveToGroup = useCallback(async (c: Case, groupId: string | null) => {
    try {
      if (groupId) {
        await assignCaseToGroup(groupId, c.id);
        toast.addToast('Moved to group');
      } else {
        const gids = (caseToGroups[c.id] || []).filter((g) => g !== archiveGroupId);
        for (const gid of gids) {
          await removeCaseFromGroup(gid, c.id);
        }
        toast.addToast('Removed from groups');
      }
      await reloadAll();
    } catch (e: any) {
      toast.addToast(e?.response?.data?.detail || 'Move failed', 'error');
    }
  }, [reloadAll, toast, caseToGroups, archiveGroupId]);

  const onCreateGroup = useCallback(async () => {
    const name = newGroupName.trim();
    if (!name) { setCreatingGroup(false); return; }
    try {
      await createCaseGroup(name);
      setNewGroupName('');
      setCreatingGroup(false);
      toast.addToast('Group created');
      await loadGroups();
    } catch (e: any) {
      toast.addToast(e?.response?.data?.detail || 'Group creation failed', 'error');
      setCreatingGroup(false);
    }
  }, [newGroupName, toast, loadGroups]);

  const onDeleteGroup = useCallback(async (g: CaseGroup) => {
    if (g.name === ARCHIVE_GROUP_NAME) return;
    try {
      await deleteCaseGroup(g.id);
      toast.addToast('Group deleted');
      await loadGroups();
    } catch (e: any) {
      toast.addToast(e?.response?.data?.detail || 'Delete failed', 'error');
    }
  }, [toast, loadGroups]);

  const onCaseDragStart = (e: React.DragEvent, c: Case) => {
    e.dataTransfer.setData('text/case-id', c.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onGroupDragOver = (e: React.DragEvent, gid: string) => {
    if (e.dataTransfer.types.includes('text/case-id')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverGroup(gid);
    }
  };
  const onGroupDrop = async (e: React.DragEvent, gid: string) => {
    e.preventDefault();
    setDragOverGroup(null);
    const caseId = e.dataTransfer.getData('text/case-id');
    if (!caseId) return;
    const c = cases.find((x) => x.id === caseId);
    if (c) await moveToGroup(c, gid);
  };

  const openContextMenu = (e: React.MouseEvent, c: Case) => {
    e.preventDefault();
    e.stopPropagation();
    setAssignSubmenu(false);
    setMoveSubmenu(false);
    setContextMenu({ caseId: c.id, x: e.clientX, y: e.clientY });
  };

  const renderCaseRow = (c: Case) => {
    const active = c.id === activeCaseId;
    const title = c.title || c.case_number || c.depositor_name || 'Untitled case';
    const entity = c.entity_name || c.issuer_name || c.depositor_name || '';
    const assignee = assignable.find((u) => u.id === c.assigned_analyst_id);
    return (
      <div
        key={c.id}
        draggable
        onDragStart={(e) => onCaseDragStart(e, c)}
        onContextMenu={(e) => openContextMenu(e, c)}
        data-testid={`case-row-${c.id}`}
        className={`group relative flex items-start gap-2 px-3 py-2.5 border-l-2 cursor-pointer transition-colors ${
          active ? 'border-primary-500 bg-primary-50' : 'border-transparent hover:bg-slate-50'
        }`}
        onClick={() => onSelectCase(c)}
      >
        {typeIcon(c.type)}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-slate-900 truncate">{title}</span>
            {c.unread && (
              <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" data-testid={`unread-${c.id}`} />
            )}
            {c.is_archived && <Archive size={11} className="text-slate-400 flex-shrink-0" />}
          </div>
          {entity && <div className="text-xs text-slate-500 truncate">{entity}</div>}
          <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1.5">
            <span>{relativeTime(c.last_activity_at || c.updated_at)}</span>
            {assignee && <span className="text-primary-500">@{assignee.username}</span>}
          </div>
        </div>
        <button
          onClick={(e) => openContextMenu(e, c)}
          className="absolute right-1.5 top-1.5 p-1 rounded text-slate-300 hover:text-slate-600 hover:bg-slate-100 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`case-more-${c.id}`}
          title="Case actions"
        >
          <MoreVertical size={14} />
        </button>
      </div>
    );
  };

  const renderGroup = (g: CaseGroup, rows: Case[]) => {
    const isCollapsed = collapsed[g.id];
    const isArchive = g.name === ARCHIVE_GROUP_NAME;
    return (
      <div key={g.id} className="border-b border-slate-50">
        <div
          onDragOver={(e) => onGroupDragOver(e, g.id)}
          onDragLeave={() => setDragOverGroup(null)}
          onDrop={(e) => onGroupDrop(e, g.id)}
          className={`flex items-center gap-1 px-2 py-1.5 select-none transition-colors ${
            dragOverGroup === g.id ? 'bg-primary-50 ring-1 ring-primary-300' : 'hover:bg-slate-50'
          }`}
        >
          <button
            onClick={() => toggleGroup(g.id)}
            className="flex items-center gap-1 flex-1 text-left min-w-0"
            data-testid={`group-header-${g.id}`}
          >
            {isCollapsed ? <ChevronRight size={14} className="text-slate-400 flex-shrink-0" /> : <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />}
            <span className="text-xs font-semibold text-slate-700 truncate">{g.name}</span>
            <span className="text-[10px] text-slate-400">({rows.length})</span>
          </button>
          {!isArchive && groups.length > 1 && (
            <button
              onClick={() => onDeleteGroup(g)}
              className="p-0.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
              title="Delete group"
              data-testid={`group-delete-${g.id}`}
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
        {!isCollapsed && (
          <div data-testid={`group-body-${g.id}`}>
            {rows.length === 0 ? (
              <div className="px-3 py-2 text-[11px] text-slate-300 italic">No cases</div>
            ) : (
              rows.map(renderCaseRow)
            )}
          </div>
        )}
      </div>
    );
  };

  const contextCase = contextMenu ? cases.find((c) => c.id === contextMenu.caseId) : null;

  return (
    <aside style={width ? { width } : undefined} className="w-[280px] flex-shrink-0 bg-white border-r border-slate-200 flex flex-col h-full select-none">
      <div className="px-3 pt-3 pb-2 border-b border-slate-100">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="text-primary-600" size={18} />
          <span className="font-semibold text-slate-900 text-sm">Documents Review</span>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input
            type="text"
            data-testid="sidebar-search"
            placeholder="Search cases..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-slate-300 bg-white text-slate-800 placeholder-slate-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
          />
        </div>
        <div className="flex gap-1 mt-2" role="tablist">
          {FILTER_TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              data-testid={`filter-${t.key}`}
              aria-selected={filter === t.key}
              onClick={() => setFilter(t.key)}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                filter === t.key ? 'bg-primary-100 text-primary-700' : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="case-list">
        {loading && (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
          </div>
        )}
        {error && <div className="px-3 py-4 text-xs text-red-600">{error}</div>}
        {!loading && !error && cases.length === 0 && (
          <div className="px-3 py-8 text-center text-xs text-slate-400">No cases found.</div>
        )}
        {!loading && !error && (
          <>
            {regularGroups.map((g) => renderGroup(g, casesByGroup[g.id] || []))}

            {ungrouped.length > 0 && (
              <div className="border-b border-slate-50">
                <div className="flex items-center gap-1 px-2 py-1.5">
                  <button
                    onClick={() => toggleGroup('__ungrouped__')}
                    className="flex items-center gap-1 flex-1 text-left min-w-0"
                    data-testid="group-header-ungrouped"
                  >
                    {collapsed['__ungrouped__'] ? <ChevronRight size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                    <span className="text-xs font-semibold text-slate-700">Ungrouped</span>
                    <span className="text-[10px] text-slate-400">({ungrouped.length})</span>
                  </button>
                </div>
                {!collapsed['__ungrouped__'] && ungrouped.map(renderCaseRow)}
              </div>
            )}

            {showArchived && archiveGroup && archivedCases.length > 0 && renderGroup(archiveGroup, archivedCases)}

            {creatingGroup ? (
              <div className="px-2 py-2 border-b border-slate-50">
                <input
                  autoFocus
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onCreateGroup(); if (e.key === 'Escape') { setCreatingGroup(false); setNewGroupName(''); } }}
                  onBlur={onCreateGroup}
                  placeholder="Group name..."
                  data-testid="new-group-input"
                  className="w-full px-2 py-1.5 text-sm rounded-md border border-primary-400 ring-1 ring-primary-300 focus:outline-none"
                />
              </div>
            ) : (
              <button
                onClick={() => setCreatingGroup(true)}
                className="w-full flex items-center gap-1.5 px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 hover:text-primary-600 transition-colors border-b border-slate-50"
                data-testid="new-group-btn"
              >
                <FolderPlus size={14} /> New Group
              </button>
            )}
          </>
        )}
      </div>

      <div className="p-3 border-t border-slate-200 space-y-2">
        <div className="flex items-center justify-between text-[11px]">
          <label className="flex items-center gap-1.5 text-slate-500 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="show-archived-toggle"
              className="accent-primary-600"
            />
            Show archived
          </label>
          <label className="flex items-center gap-1.5 text-slate-500 cursor-pointer">
            <input
              type="checkbox"
              checked={mineOnly}
              onChange={(e) => setMineOnly(e.target.checked)}
              data-testid="mine-only-toggle"
              className="accent-primary-600"
            />
            My cases only
          </label>
        </div>
        <button onClick={onNewCase} className="btn-primary w-full justify-center" data-testid="new-case-btn">
          <Plus size={16} /> New Case
        </button>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setShowAbout(true)}
            className="text-[11px] text-slate-400 hover:text-primary-600 transition-colors cursor-pointer"
            title="About this build"
            data-testid="about-link"
          >
            {RC_LABEL}{envProfile && ` · ${envProfile}`}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="text-slate-400 hover:text-primary-600 transition-colors flex items-center"
            title="Settings & Configuration"
            data-testid="settings-link"
          >
            <Settings size={14} />
          </button>
        </div>
      </div>

      {showAbout && <AboutModal info={aboutInfo} onClose={() => setShowAbout(false)} />}

      {contextMenu && contextCase && (
        <div
          className="fixed z-50 min-w-[180px] bg-white rounded-lg shadow-lg border border-slate-200 py-1 text-sm"
          style={{ left: Math.min(contextMenu.x, window.innerWidth - 200), top: Math.min(contextMenu.y, window.innerHeight - 220) }}
          data-testid="case-context-menu"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => { toggleArchive(contextCase); setContextMenu(null); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-slate-700 hover:bg-slate-100"
            data-testid="ctx-archive"
          >
            {contextCase.is_archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
            {contextCase.is_archived ? 'Unarchive' : 'Archive'}
          </button>

          <div className="relative">
            <button
              onClick={() => { setAssignSubmenu(!assignSubmenu); setMoveSubmenu(false); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-slate-700 hover:bg-slate-100"
              data-testid="ctx-assign"
            >
              <UserPlus size={14} /> Assign to...
            </button>
            {assignSubmenu && (
              <div className="ml-6 mb-1 border-l border-slate-200">
                {contextCase.assigned_analyst_id && (
                  <button
                    onClick={() => { assignTo(contextCase, null); setContextMenu(null); }}
                    className="w-full flex items-center gap-2 px-3 py-1 text-left text-xs text-slate-500 hover:bg-slate-100"
                  >
                    Clear assignment
                  </button>
                )}
                {assignable.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => { assignTo(contextCase, u.id); setContextMenu(null); }}
                    className={`w-full flex items-center gap-2 px-3 py-1 text-left text-xs hover:bg-slate-100 ${u.id === contextCase.assigned_analyst_id ? 'text-primary-600 font-medium' : 'text-slate-600'}`}
                    data-testid={`ctx-assign-${u.id}`}
                  >
                    {u.full_name} <span className="text-slate-400">({u.role})</span>
                  </button>
                ))}
                {assignable.length === 0 && (
                  <div className="px-3 py-1 text-[11px] text-slate-400">No assignable users</div>
                )}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => { setMoveSubmenu(!moveSubmenu); setAssignSubmenu(false); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-slate-700 hover:bg-slate-100"
              data-testid="ctx-move"
            >
              <FolderInput size={14} /> Move to group...
            </button>
            {moveSubmenu && (
              <div className="ml-6 mb-1 border-l border-slate-200 max-h-40 overflow-y-auto">
                {regularGroups.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => { moveToGroup(contextCase, g.id); setContextMenu(null); }}
                    className="w-full flex items-center gap-2 px-3 py-1 text-left text-xs text-slate-600 hover:bg-slate-100"
                    data-testid={`ctx-move-${g.id}`}
                  >
                    {g.name}
                  </button>
                ))}
                {regularGroups.length === 0 && (
                  <div className="px-3 py-1 text-[11px] text-slate-400">No groups</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}